/**
 * ResponsiveCabinetCollectionCard - A responsive card component for displaying cabinet collections
 *
 * Features:
 * - Responsive layout that adapts between mobile and desktop views
 * - Optional hover effect on images (switches between primary/secondary images)
 * - Configurable action buttons with customizable labels and handlers
 * - Consistent styling with SUI Atomic Components
 *
 * Layout Behavior:
 * - Mobile: Vertical layout (flex-col) with centered media and full-width buttons
 * - Desktop: Horizontal layout (flex-row) with side-by-side media and content
 * - Breakpoint: Transitions at min-width 430px for mobile devices
 *
 * Button Configuration:
 * - Primary button: Typically used for "Select Collection" action
 * - Secondary button: Typically used for "View Details" action
 * - Both buttons support custom labels, disabled states, and click handlers
 *
 * @component
 * @example
 * // Basic usage
 * <ResponsiveCabinetCollectionCard
 *   collection={{
 *     productName: "Modern Kitchen Cabinets",
 *     brandName: "Brand Name",
 *     primaryURL: "https://example.com/primary.jpg",
 *     secondaryURL: "https://example.com/secondary.jpg",
 *     designStyleChoiceId: "12345",
 *     itemId: "67890"
 *   }}
 *   onPrimaryActionClick={(styleId, itemId) => handleSelect(styleId, itemId)}
 *   onSecondaryActionClick={(productURL) => handleViewDetails(productURL)}
 * />
 *
 * @example
 * // With custom configuration
 * <ResponsiveCabinetCollectionCard
 *   collection={collectionData}
 *   hoverableMedia={true}
 *   isMobile={isMobileDevice}
 *   primaryActionButtonLabel="Add to Cart"
 *   secondaryActionButtonLabel="Learn More"
 *   primaryActionButtonDisabled={!inStock}
 *   onPrimaryActionClick={handleAddToCart}
 *   onSecondaryActionClick={handleLearnMore}
 * />
 *
 * @param {Object} props - Component props
 * @param {Object} props.collection - Collection data object containing product information
 *
 * @param {boolean} [props.hoverableMedia=false] - Enable hover effect to switch between primary/secondary images
 * @param {boolean} [props.isMobile=false] - Force mobile layout (auto-detected if not provided)
 *
 * @param {Function} props.onPrimaryActionClick - Primary button click handler
 * @param {Function} props.onSecondaryActionClick - Secondary button click handler
 *
 * @param {string} [props.primaryActionButtonLabel="Select Collection"] - Text label for primary button
 * @param {string} [props.secondaryActionButtonLabel="View"] - Text label for secondary button
 * @param {boolean} [props.primaryActionButtonDisabled=false] - Disable state for primary button
 * @param {boolean} [props.secondaryActionButtonDisabled=false] - Disable state for secondary button
 *
 * @returns {JSX.Element} Responsive cabinet collection card component
 *
 * @author FAX8TXL
 */

import React, { useContext } from 'react';
import { func, bool, objectOf, string } from 'prop-types';
import {
  Button,
  CardActions,
  CardBody,
  CardMedia,
  CardTitle,
  Typography,
} from '@one-thd/sui-atomic-components';
import classNames from 'classnames';
import { Price } from '@thd-olt-component-react/price';
import {
  params, string as stringType, shape, arrayOf, extend, useDataModel
} from '@thd-nucleus/data-sources';
import { Promotion } from '@thd-olt-component-react/promotion';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import _isEmpty from 'lodash/isEmpty';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { getLocalizerZipCode } from '../../../../../utils/fulfillment-utils';
import { HoverableCardMedia } from '../HoverableCardMedia';
import { ProductPod } from '../../../../ProductPod';

const ResponsiveCabinetCollectionCard = ({
  collection, hoverableMedia, onPrimaryActionClick,
  onSecondaryActionClick,
  primaryActionButtonLabel,
  secondaryActionButtonLabel,
  primaryActionButtonDisabled,
  secondaryActionButtonDisabled,
  isMobile,
}) => {
  const store = useStore();
  const { storeId } = store;
  const zipCode = getLocalizerZipCode();

  const {
    data,
    error,
    loading
  } = useDataModel('searchModel', {
    variables: {
      itemIds: [collection.sampleBaseCabinetItemId],
      storeId,
      zipCode,
      dataSource: 'searchNav'
    },
    fetchPolicy: 'cache-first',
    ssr: storeId === '8119',
    skip: _isEmpty(collection.sampleBaseCabinetItemId)
  });

  const cabinetCollectionCardActionsStyles = isMobile ? {
    display: 'flex',
    gap: '4px',
    flexDirection: 'column',
    width: '100%',
    marginTop: '2rem',
    alignItems: 'center'
  } : {
    display: 'flex',
    gap: '8px',
    flexDirection: 'row',
    justifyContent: 'space-between'
  };

  const cardContainerClasses = classNames(
    'sui-paper sui-paper-outlined sui-rounded-base sui-flex sui-rounded-md sui-overflow-hidden sui-p-4 sui-gap-4',
    isMobile ? 'sui-flex-col min-[430px]:sui-flex-row' : ''
  );

  const cardMediaClasses = classNames(
    'sui-flex',
    isMobile && [
      'sui-justify-center',
      'sui-flex-col',
      'sui-items-center',
      'min-[430px]:sui-w-[170px]',
      'sui-h-[170px]',
      'sui-shrink-0',
      'sui-overflow-hidden'
    ].join(' '),
    !isMobile && 'sui-basis-2/5'
  );

  const cardContentClasses = classNames();

  const { channel } = useContext(ExperienceContext);
  const productData = {
    pricing: {
      alternatePriceDisplay: false,
      conditionalPromotions: null,
      mapAboveOriginalPrice: null,
      message: 'Starting at',
      original: 0,
      preferredPriceFlag: false,
      promotion: {
        dollarOff: 0,
        percentageOff: 0,
      },
      specialBuy: null,
      unitOfMeasure: 'each',
      value: collection.startingAtPrice
    },
  };

  return (
    <div className={cardContainerClasses}>
      <div className={cardMediaClasses}>
        {hoverableMedia ? (
          <HoverableCardMedia
            primaryURL={collection.primaryURL}
            secondaryURL={collection.secondaryURL}
            alt={collection.productName}
            aspect="square"
          />
        ) : (
          <CardMedia
            aspect="square"
            src={collection.primaryURL}
            alt={collection.productName}
          />
        )}
      </div>
      <div className="sui-flex sui-basis-3/5 sui-flex-col">
        <CardTitle
          disableTypography
          header={(
            <Typography
              variant="h5"
              weight="bold"
              className="wrap-break-word hyphens-auto overflow-hidden"
            >
              {collection.productLabel}
            </Typography>
          )}
          subheader={(
            <Typography
              className="overflow-hidden text-ellipsis"
            >{collection.brandName}
            </Typography>
          )}
        />
        <CardBody className="sui-overflow-hidden sui-break-words">
          <div className="sui-mb-2">
            {!loading && collection.sampleBaseCabinetItemId && (
              <Promotion
                channel={channel}
                itemId={collection.sampleBaseCabinetItemId}
                pageType="PLP"
                hideConditionalPromoDescription
              />
            )}
          </div>
          <div className="sui-mb-4 sui-mt-4">
            <Typography
              variant="h6"
              weight="bold"
              color="Primary"
              className="sui-text-xl"
            >
              <Price
                itemId={collection.itemId}
                skip
                channel={channel}
                type="simple"
                displayEachUom={false}
                clsRemediation={{
                  placeholders: true,
                  preservePlaceholders: true
                }}
                product={productData}
              />
            </Typography>
          </div>
        </CardBody>
        <CardActions
          style={cabinetCollectionCardActionsStyles}
        >
          <Button
            variant="secondary"
            size="small"
            onClick={() => onPrimaryActionClick(collection)}
            style={{
              flexShrink: 0,
            }}
            disabled={primaryActionButtonDisabled}
          >
            {primaryActionButtonLabel}
          </Button>
          <Button
            variant="ghost"
            disabled={secondaryActionButtonDisabled}
            size="small"
            onClick={() => onSecondaryActionClick(collection.productURL)}
            style={{
              flexShrink: 0,
            }}
          >
            {secondaryActionButtonLabel}
          </Button>
        </CardActions>
      </div>
    </div>
  );
};

export default ResponsiveCabinetCollectionCard;

const Collection = {
  searchModel: params({
    itemIds: arrayOf(stringType()).isRequired(),
    storeId: stringType().isRequired()
  }).shape({
    products: arrayOf(shape({
      info: shape({
        categoryHierarchy: arrayOf(stringType())
      }),
      ...ProductPod.dataModel.product,
    }))
  }),
};

const CollectionDataModel = extend(
  Breadcrumbs,
  ProductPod.dataModel,
  Collection
);

ResponsiveCabinetCollectionCard.dataModel = CollectionDataModel;

ResponsiveCabinetCollectionCard.propTypes = {
  hoverableMedia: bool,
  collection: objectOf(string).isRequired,
  // Button Actions
  onPrimaryActionClick: func.isRequired,
  onSecondaryActionClick: func.isRequired,
  // Button Labels
  primaryActionButtonLabel: string,
  secondaryActionButtonLabel: string,
  // Button States
  primaryActionButtonDisabled: bool,
  secondaryActionButtonDisabled: bool,
  isMobile: bool,
};
ResponsiveCabinetCollectionCard.defaultProps = {
  hoverableMedia: false,
  primaryActionButtonLabel: 'Select Collection',
  secondaryActionButtonLabel: 'View',
  primaryActionButtonDisabled: false,
  secondaryActionButtonDisabled: false,
  isMobile: false,
};
